import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react";
import {
    computeScalarField,
    marchingSquares,
    type GridPoint,
} from "../../utils/marchingSquares";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";

export interface ContourLevel {
    paths: string[];
    level: number;
    threshold: number;
}

interface BlobFieldState {
    // Grid structure
    columns: number;
    rows: number;
    cellSize: number;
    // Randomisation
    seed: string;
    pointDensity: number;
    // Blob shape
    influenceRadius: number;
    baseThreshold: number;
    contourLevels: number;
    contourStep: number;
    // Appearance
    showGridPoints: boolean;
    fillColor: string;
    strokeColor: string;
    // Setters
    setColumns: (v: number) => void;
    setRows: (v: number) => void;
    setCellSize: (v: number) => void;
    setSeed: (v: string) => void;
    setPointDensity: (v: number) => void;
    setInfluenceRadius: (v: number) => void;
    setBaseThreshold: (v: number) => void;
    setContourLevels: (v: number) => void;
    setContourStep: (v: number) => void;
    setShowGridPoints: (v: boolean) => void;
    setFillColor: (v: string) => void;
    setStrokeColor: (v: string) => void;
    // Derived
    canvasW: number;
    canvasH: number;
    viewBoxAttr: string;
    gridPoints: GridPoint[];
    contourData: ContourLevel[];
    canExport: boolean;
    buildExportSvgString: () => string;
    handleDownload: () => void;
    randomizeParameters: () => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
}

const BlobFieldContext = createContext<BlobFieldState | null>(null);

const DEFAULT_BLOB_FIELD_PRESET: Record<string, unknown> = {
    columns: 30,
    rows: 30,
    cellSize: 50,
    seed: "1",
    pointDensity: 0.2,
    influenceRadius: 0.7,
    baseThreshold: 0.15,
    contourLevels: 3,
    contourStep: 0.08,
    showGridPoints: true,
    fillColor: "#555555",
    strokeColor: "#333333",
};

export function useBlobField() {
    const ctx = useContext(BlobFieldContext);
    if (!ctx)
        throw new Error("useBlobField must be used within BlobFieldProvider");
    return ctx;
}

/** LCG seeded from a string – same pattern used across the project. */
function seededRNG(seed: string): () => number {
    let s = [...seed].reduce((a, c) => a + c.charCodeAt(0), 0) || 1;
    return () => {
        s = (s * 9301 + 49297) % 233280;
        return s / 233280;
    };
}

export function BlobFieldProvider({ children }: { children: React.ReactNode }) {
    const [columns, setColumns] = useState(30);
    const [rows, setRows] = useState(30);
    const [cellSize, setCellSize] = useState(50);
    const [seed, setSeed] = useState("1");
    const [pointDensity, setPointDensity] = useState(0.2);
    const [influenceRadius, setInfluenceRadius] = useState(0.7);
    const [baseThreshold, setBaseThreshold] = useState(0.15);
    const [contourLevels, setContourLevels] = useState(3);
    const [contourStep, setContourStep] = useState(0.08);
    const [showGridPoints, setShowGridPoints] = useState(true);
    const [fillColor, setFillColor] = useState("#555555");
    const [strokeColor, setStrokeColor] = useState("#333333");

    const canvasW = Math.max(1, columns - 1) * cellSize;
    const canvasH = Math.max(1, rows - 1) * cellSize;
    const padding = cellSize * 0.5;
    const viewBoxAttr = `${-padding} ${-padding} ${canvasW + padding * 2} ${canvasH + padding * 2}`;

    const gridPoints = useMemo<GridPoint[]>(() => {
        const rng = seededRNG(seed);
        const pts: GridPoint[] = [];
        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < columns; c++) {
                const active = rng() < pointDensity;
                // Second draw for the weight magnitude (0.5–1.5 range)
                const raw = rng();
                pts.push({
                    x: c * cellSize,
                    y: r * cellSize,
                    weight: active ? 0.5 + raw : 0,
                });
            }
        }
        return pts;
    }, [columns, rows, cellSize, seed, pointDensity]);

    const sampleStep = useMemo(
        () => Math.max(2, Math.round(cellSize / 8)),
        [cellSize],
    );

    const scalarField = useMemo(() => {
        const sigma = cellSize * influenceRadius;
        return computeScalarField(
            gridPoints,
            canvasW,
            canvasH,
            sampleStep,
            sigma,
        );
    }, [gridPoints, canvasW, canvasH, sampleStep, cellSize, influenceRadius]);

    const contourData = useMemo<ContourLevel[]>(() => {
        const levels: ContourLevel[] = [];
        for (let i = 0; i < contourLevels; i++) {
            const threshold = baseThreshold + i * contourStep;
            const paths = marchingSquares(scalarField, sampleStep, threshold);
            levels.push({ paths, level: i, threshold });
        }
        return levels;
    }, [scalarField, sampleStep, baseThreshold, contourLevels, contourStep]);

    const canExport = contourData.some((l) => l.paths.length > 0);

    const buildExportSvgString = (): string => {
        const gridDots = showGridPoints
            ? gridPoints
                  .map((pt) => {
                      const r = pt.weight > 0 ? 3 : 1.5;
                      const op = pt.weight > 0 ? 1 : 0.3;
                      return `<circle cx="${pt.x}" cy="${pt.y}" r="${r}" fill="${strokeColor}" opacity="${op}" />`;
                  })
                  .join("\n    ")
            : "";

        const contourPaths = contourData
            .map(({ paths, level }) => {
                const opacity = ((level + 1) / contourLevels).toFixed(3);
                return paths
                    .map(
                        (d) =>
                            `<path d="${d}" fill="${fillColor}" fill-opacity="${opacity}" stroke="${strokeColor}" stroke-width="0.5" stroke-opacity="0.6" />`,
                    )
                    .join("\n    ");
            })
            .join("\n    ");

        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxAttr}">
  <rect x="${-padding}" y="${-padding}" width="${canvasW + padding * 2}" height="${canvasH + padding * 2}" fill="white" />
  <g class="contours">
    ${contourPaths}
  </g>
  <g class="grid-points">
    ${gridDots}
  </g>
</svg>`;
    };

    const handleDownload = () => {
        if (!canExport) return;
        const svgString = buildExportSvgString();
        const blob = new Blob([svgString], {
            type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `blob-field-${seed}-${Date.now()}.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            columns, rows, cellSize, seed, pointDensity,
            influenceRadius, baseThreshold, contourLevels, contourStep,
            showGridPoints,
            fillColor, strokeColor,
        }),
        [
            columns, rows, cellSize, seed, pointDensity,
            influenceRadius, baseThreshold, contourLevels, contourStep,
            showGridPoints,
            fillColor, strokeColor,
        ],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.columns != null) setColumns(values.columns as number);
        if (values.rows != null) setRows(values.rows as number);
        if (values.cellSize != null) setCellSize(values.cellSize as number);
        if (values.seed != null) setSeed(values.seed as string);
        if (values.pointDensity != null) setPointDensity(values.pointDensity as number);
        if (values.influenceRadius != null) setInfluenceRadius(values.influenceRadius as number);
        if (values.baseThreshold != null) setBaseThreshold(values.baseThreshold as number);
        if (values.contourLevels != null) setContourLevels(values.contourLevels as number);
        if (values.contourStep != null) setContourStep(values.contourStep as number);
        if (values.showGridPoints != null) {
            setShowGridPoints(values.showGridPoints as boolean);
        }
        if (values.fillColor != null) setFillColor(values.fillColor as string);
        if (values.strokeColor != null) setStrokeColor(values.strokeColor as string);
    }, []);

    const { resetParameters } = useToolParamPersistence({
        toolId: "blob-field",
        defaultPreset: DEFAULT_BLOB_FIELD_PRESET,
        getPresetValues,
        applyPreset,
    });

    const randomizeParameters = useCallback(() => {
        const rng = seededRNG(Date.now().toString());
        const randI = (min: number, max: number) =>
            Math.floor(rng() * (max - min + 1)) + min;
        const randF = (min: number, max: number) => rng() * (max - min) + min;
        const byte = (lo: number, hi: number) =>
            Math.floor(rng() * (hi - lo + 1) + lo)
                .toString(16)
                .padStart(2, "0");

        setSeed(Math.floor(rng() * 9000 + 1000).toString());
        setColumns(randI(12, 36));
        setRows(randI(12, 36));
        setCellSize(Math.round(randF(20, 95) / 5) * 5);
        setPointDensity(parseFloat(randF(0.08, 0.48).toFixed(2)));
        setInfluenceRadius(parseFloat(randF(0.35, 3.2).toFixed(1)));
        setBaseThreshold(parseFloat(randF(0.05, 0.32).toFixed(2)));
        setContourLevels(randI(2, 6));
        setContourStep(parseFloat(randF(0.04, 0.16).toFixed(2)));
        setShowGridPoints(rng() < 0.55);
        setFillColor(`#${byte(35, 130)}${byte(35, 130)}${byte(40, 150)}`);
        setStrokeColor(`#${byte(12, 70)}${byte(12, 70)}${byte(15, 80)}`);
    }, []);

    const value: BlobFieldState = {
        columns,
        rows,
        cellSize,
        seed,
        pointDensity,
        influenceRadius,
        baseThreshold,
        contourLevels,
        contourStep,
        showGridPoints,
        fillColor,
        strokeColor,
        setColumns,
        setRows,
        setCellSize,
        setSeed,
        setPointDensity,
        setInfluenceRadius,
        setBaseThreshold,
        setContourLevels,
        setContourStep,
        setShowGridPoints,
        setFillColor,
        setStrokeColor,
        canvasW,
        canvasH,
        viewBoxAttr,
        gridPoints,
        contourData,
        canExport,
        buildExportSvgString,
        handleDownload,
        randomizeParameters,
        applyPreset,
        getPresetValues,
        resetParameters,
    };

    return (
        <BlobFieldContext.Provider value={value}>
            {children}
        </BlobFieldContext.Provider>
    );
}
